/**
 * The VIVA wordmark and app mark.
 *
 * The wordmark is set in Playfair Display with a gold leaf standing in for the
 * dot on the "i" — the accent the brand system describes as sun, growth and
 * Brazilian warmth. It is composed rather than drawn as outlines so it stays
 * live text for screen readers and scales with `font-size`; the leaf is placed
 * in `em` units so it tracks the type at any size.
 */

interface WordmarkProps {
  /** Any font-size utility; every part of the mark scales from it. */
  className?: string
  /** `light` inverts the letterforms for use on forest green. */
  tone?: 'forest' | 'light'
}

function Leaf({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} style={style} aria-hidden="true">
      <path
        d="M21.5 2.6c0 9.9-7.2 16.4-16.6 16.4C4.9 9.1 12.1 2.6 21.5 2.6Z"
        fill="currentColor"
      />
      <path
        d="M18.4 5.9C13.6 8.2 9.6 12.6 7.9 17.6"
        stroke="#0F3D2E"
        strokeWidth="1.4"
        strokeLinecap="round"
        opacity="0.28"
      />
    </svg>
  )
}

export function VivaWordmark({ className = 'text-3xl', tone = 'forest' }: WordmarkProps) {
  return (
    <span className={`inline-flex items-baseline ${className}`}>
      <span
        aria-hidden="true"
        className={`relative font-display leading-none font-bold tracking-tight ${
          tone === 'light' ? 'text-white' : 'text-brand-600'
        }`}
      >
        {/* U+0131, the dotless i, so the leaf *replaces* the tittle instead of
            being pasted on top of it. Verified present in the bundled Playfair
            subset, and it carries the same advance width as a normal "i", so
            the wordmark's spacing is unchanged. */}
        v&#x131;va
        {/* Measured from the font: "v" is 0.508em wide and the i 0.315em, so
            0.6655em is the i's centre. Everything is in em, so the leaf tracks
            the type at any size. */}
        <Leaf
          className="absolute -translate-x-1/2 text-gold-500"
          style={{ width: '0.32em', height: '0.32em', left: '0.6655em', top: '0.02em' }}
        />
      </span>
      <span className="sr-only">VIVA</span>
    </span>
  )
}

/**
 * The square mark: a "v" with the same leaf, for the app icon and any place
 * too tight for the full wordmark.
 */
export function VivaMark({ className = 'size-10' }: { className?: string }) {
  return (
    <span
      className={`relative inline-flex items-center justify-center rounded-[28%] bg-brand-600 ${className}`}
      aria-hidden="true"
    >
      <span className="font-display text-[0.62em] leading-none font-bold text-white">v</span>
      <Leaf
        className="absolute text-gold-500"
        style={{ width: '0.26em', height: '0.26em', right: '0.2em', top: '0.18em' }}
      />
    </span>
  )
}

/** Wordmark plus the descriptor lockup, for the login screen. */
export function VivaLockup({ tone = 'forest' }: { tone?: 'forest' | 'light' }) {
  return (
    <div className="flex flex-col items-center">
      <VivaWordmark className="text-6xl" tone={tone} />
      <p
        className={`mt-2 text-[0.68rem] font-semibold tracking-[0.28em] uppercase ${
          tone === 'light' ? 'text-brand-100' : 'text-brand-500'
        }`}
      >
        Portuguese Learning
      </p>
    </div>
  )
}
